/**
 * YAML Generation Utilities
 * Converts RedteamConfig to YAML format for promptfoo
 */

import yaml from "js-yaml";
import type { RedteamConfig } from "@/types/redteam";

interface UnifiedConfig {
  description?: string;
  targets: Array<{
    id: string;
    label?: string;
    config: Record<string, unknown>;
  }>;
  prompts: string[];
  extensions?: string[];
  defaultTest?: {
    description?: string;
    vars?: Record<string, unknown>;
    provider?: string | Record<string, unknown>;
    options?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
  };
  redteam?: {
    purpose?: string;
    numTests?: number;
    plugins: Array<{ id: string; config?: Record<string, unknown> }>;
    strategies: Array<{ id: string; config?: Record<string, unknown> }>;
    language?: string | string[];
    maxConcurrency?: number;
    testGenerationInstructions?: string;
    frameworks?: string[];
  };
}

/**
 * The unified config structure that matches promptfoo's expected format.
 * This is exported so it can be used by prepareConfigForSubmission.
 */
export type { UnifiedConfig };

/**
 * Converts RedteamConfig to UnifiedConfig format for YAML generation
 * Also exported for use in prepareConfigForSubmission
 */
export function convertToUnifiedConfig(config: RedteamConfig): UnifiedConfig {
  // Convert plugins - always output as objects with id (matching promptfoo format)
  const plugins = config.plugins.map((plugin) => {
    if (typeof plugin === "string") {
      return { id: plugin };
    }
    // Plugin object
    const pluginObj: { id: string; config?: Record<string, unknown> } = {
      id: plugin.id,
    };

    if (plugin.config && Object.keys(plugin.config).length > 0) {
      pluginObj.config = plugin.config as Record<string, unknown>;
    }

    return pluginObj;
  });

  // Convert strategies - always output as objects with id (matching promptfoo format)
  const strategies = config.strategies.map((strategy) => {
    if (typeof strategy === "string") {
      return { id: strategy };
    }
    // Strategy object
    const strategyObj: { id: string; config?: Record<string, unknown> } = {
      id: strategy.id,
    };

    if (strategy.config && Object.keys(strategy.config).length > 0) {
      strategyObj.config = strategy.config as Record<string, unknown>;
    }

    return strategyObj;
  });

  // Build unified config
  const unified: UnifiedConfig = {
    description: config.description,
    targets: [
      {
        id: config.target.id,
        ...(config.target.label && { label: config.target.label }),
        /**
         * Target configuration with optional delay override.
         * config.target.delay (optional number, 0 is valid) overrides any delay in config.target.config.
         * Delay is specified in milliseconds and adds a pause between test requests to prevent overwhelming the API endpoint.
         */
        config: {
          ...config.target.config,
          ...(config.target.delay !== undefined && { delay: config.target.delay }),
        },
      },
    ],
    prompts: config.prompts,
    ...(config.extensions && config.extensions.length > 0 && { extensions: config.extensions }),
    redteam: {
      ...(config.purpose && { purpose: config.purpose }),
      ...(config.numTests && { numTests: config.numTests }),
      plugins,
      strategies,
      ...(config.language && { language: config.language }),
      ...(config.maxConcurrency && { maxConcurrency: config.maxConcurrency }),
      ...(config.testGenerationInstructions && {
        testGenerationInstructions: config.testGenerationInstructions,
      }),
      ...(config.frameworks && config.frameworks.length > 0 && { frameworks: config.frameworks }),
    },
  };

  // Always include defaultTest with transformVars for session tracking (matching promptfoo)
  unified.defaultTest = {
    ...(config.defaultTest?.description && { description: config.defaultTest.description }),
    ...(config.defaultTest?.vars && { vars: config.defaultTest.vars }),
    ...(config.defaultTest?.provider &&
      (typeof config.defaultTest.provider === "string"
        ? { provider: config.defaultTest.provider }
        : {
            provider: {
              id: config.defaultTest.provider.id,
              ...(config.defaultTest.provider.label && {
                label: config.defaultTest.provider.label,
              }),
              ...config.defaultTest.provider.config,
            } as Record<string, unknown>,
          })),
    options: {
      ...(config.defaultTest?.options || {}),
      // Always include transformVars for session tracking (required by promptfoo)
      transformVars: "{ ...vars, sessionId: context.uuid }",
    },
    ...(config.defaultTest?.metadata && { metadata: config.defaultTest.metadata }),
  };

  return unified;
}

/**
 * Orders the config object according to promptfoo YAML structure conventions
 */
function orderConfig(config: UnifiedConfig): UnifiedConfig {
  const orderedConfig: UnifiedConfig = {} as UnifiedConfig;

  // Order root-level keys
  const rootOrder: Array<keyof UnifiedConfig> = [
    "description",
    "targets",
    "prompts",
    "extensions",
    "defaultTest",
    "redteam",
  ];

  rootOrder.forEach((key) => {
    if (key in config) {
      (orderedConfig as any)[key] = config[key];
    }
  });

  // Order redteam keys if present
  if (config.redteam) {
    const orderedRedteam: typeof config.redteam = {} as typeof config.redteam;
    const redteamOrder: Array<keyof NonNullable<UnifiedConfig["redteam"]>> = [
      "purpose",
      "numTests",
      "frameworks",
      "plugins",
      "strategies",
      "testGenerationInstructions",
      "language",
      "maxConcurrency",
    ];

    redteamOrder.forEach((key) => {
      if (key in config.redteam!) {
        (orderedRedteam as any)[key] = config.redteam![key];
      }
    });

    orderedConfig.redteam = orderedRedteam;
  }

  return orderedConfig;
}

/**
 * Generates YAML string from RedteamConfig
 */
export function generateYaml(config: RedteamConfig): string {
  const unifiedConfig = convertToUnifiedConfig(config);
  const orderedConfig = orderConfig(unifiedConfig);

  const processedConfig = JSON.parse(JSON.stringify(orderedConfig)) as UnifiedConfig;
  if (processedConfig.targets && Array.isArray(processedConfig.targets)) {
    processedConfig.targets = processedConfig.targets.map((target: any) => {
      if (target.config?.headers && typeof target.config.headers === "object") {
        const headers: Record<string, string> = {};
        for (const [key, value] of Object.entries(target.config.headers)) {
          headers[key] = String(value).replace(/\n/g, " ").trim();
        }
        target.config.headers = headers;
      }
      return target;
    });
  }

  // Generate YAML with proper formatting
  const yamlString = yaml.dump(processedConfig, {
    noRefs: true,
    lineWidth: -1, // No line wrapping
    indent: 2,
    quotingType: '"',
    forceQuotes: false,
  });

  /**
   * Post-processing step: Convert YAML folded scalars to quoted strings
   *
   * js-yaml emits folded scalars (">-" or ">") for multi-line string values, which
   * preserves line breaks and indentation. However, downstream code expects single-line
   * string values, so we convert these folded scalars into flattened quoted strings.
   *
   * Regex pattern breakdown:
   * - (^\s+): Captures leading indentation (assumes 2-space indentation per level)
   * - ([A-Za-z][A-Za-z0-9_-]*): Matches property names starting with a letter, followed
   *   by letters, digits, underscores, or hyphens (assumes standard YAML key naming)
   * - :\s*: Matches colon and optional whitespace
   * - >-?\s*: Matches folded scalar indicator ">" or ">-" (the hyphen strips trailing newline)
   * - \n: Matches the newline after the folded scalar indicator
   * - ((?:\s+.*\n?)+): Captures the folded content (one or more indented lines)
   *
   * Assumptions:
   * - Indentation follows 2-space increments (matches yaml.dump indent: 2)
   * - Property names follow standard YAML key patterns (letter-start, alphanumeric + _-)
   * - Folded values are properly indented relative to their parent key
   *
   * The replacement function:
   * - Splits folded content by newlines
   * - Trims each line and filters empty lines
   * - Joins remaining lines with spaces to create a single-line string
   * - Wraps the result in double quotes
   */
  const fixedYaml = yamlString.replace(
    /(^\s+)([A-Za-z][A-Za-z0-9_-]*):\s*>-?\s*\n((?:\s+.*\n?)+)/gm,
    (_match: string, indent: string, headerName: string, foldedValue: string) => {
      const value = foldedValue
        .split("\n")
        .map((line: string) => line.trim())
        .filter((line: string) => line.length > 0)
        .join(" ");
      return `${indent}${headerName}: "${value}"`;
    }
  );

  // Add schema comment at the top
  return `# yaml-language-server: $schema=https://promptfoo.dev/config-schema.json\n${fixedYaml}`;
}
